import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function EalingCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Ealing Pothole Claims | £8.5m Minimum to Stand Still | Fixtyer"
        description="Ealing Council says £8.5m a year is needed to stop its roads declining — yet projected 2025/26 spend falls short and 9% of its 490km of residential streets are in RED condition. See what the DfT AMBER rating means for your claim."
        canonical="/councils/ealing"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Ealing', url: '/councils/ealing' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Ealing has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend rating reflects that Ealing invests well beyond its £945,000 capital allocation. But your claim turns on the specific defect, not aggregate spend — and Ealing is AMBER on condition because B, C and unclassified roads are not improving. Section 58 requires proof the council reasonably knew about and repaired the pothole that damaged your vehicle.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 490km — 82% of Ealing\'s 597km road network. At the last survey in 2024, 9% of U-roads were in RED condition, roughly 44km of residential streets. The council collects U-road condition data annually via walked Detailed Visual Inspection surveys, but admits the trend is only "remaining constant" while budgets are stretched in real terms.',
          },
          {
            question: 'Does the £8.5m steady-state budget admission help my claim?',
            answer: 'It can. Ealing\'s own transparency report states that a minimum annual budget of £8.5m is required to maintain the network at its current condition — and that without it, "the road network will continue to decline." Projected 2025/26 capital and revenue spend totals roughly £7.66m, below that threshold. That is a council admission it is underfunding maintenance relative to its own consultant\'s model.',
          },
          {
            question: 'A-roads are improving — does that weaken claims on main roads?',
            answer: 'On principal routes, Ealing\'s position is stronger: RED-condition A-roads fell from 7% in 2020 to 3% in 2024, with TfL collecting data annually. But the council also admits it has received no TfL principal road renewal funding for two years, relying on DfT pothole grants for its busiest resurfacing. A claim on an A-road still depends on whether the specific defect was reasonably inspected and repaired.',
          },
          {
            question: 'Pothole repairs fell to 3,027 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Ealing filled 3,027 potholes in 2024/25, down from 4,241 the year before. Over five years the council repaired 17,297 potholes — about 9.5 every day. For 2025/26 it estimates an average of 3,500 pothole repairs plus 500 additional pre-emptive locations that would not normally meet safety intervention levels.',
          },
          {
            question: 'What does the AMBER best-practice rating mean for Section 58?',
            answer: 'Ealing scores AMBER on best practice alongside AMBER on condition — not the full GREEN profile of better-rated authorities. The council does run preventative programmes (73.7% of projected 2025/26 spend) and annual condition surveys, but also documents inflation pressure, no TfL principal renewal funding for two years, and a network "not designed and built" for modern vehicle weights. Specificity — prior reports, photos, road class — matters more than the headline rating.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Ealing Council"
        councilSlug="ealing"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/ealing"
        headline="Ealing Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Ealing</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Ealing Needs £8.5m Just to Stand Still — and Admits the Network Will Decline
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Ealing Council's own consultant calculated a <strong className="text-white">£8.5m minimum annual budget</strong> to
            hold road condition steady — then warned that without it, the network will continue to decline.
            The DfT gives GREEN for spend, but the overall rating is <strong className="text-amber-400">AMBER</strong> because
            <strong className="text-rose-400"> 9% of 490km of residential streets</strong> remain in RED condition while
            projected 2025/26 maintenance spend falls roughly £840,000 short of that steady-state figure.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">490km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 82% of the borough</div>
            <div className="text-brand-300 text-sm mt-1">
              9% in RED condition at the 2024 survey — approximately 44km of residential streets,
              estate roads and local routes across Acton, Ealing, Greenford, Hanwell, Northolt, Perivale and Southall.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Ealing's own transparency report — A-roads improving,
            B/C roads flat, residential streets stuck at 9-10% RED
          </p>

          {/* Network size */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Ealing's 597km road network</h3>
            <div className="grid sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">81km</div>
                <div className="text-sm text-surface-600">A-roads</div>
                <div className="text-xs text-surface-500">14% of network</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">26km</div>
                <div className="text-sm text-surface-600">B and C roads</div>
                <div className="text-xs text-surface-500">4% of network</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">490km</div>
                <div className="text-sm text-rose-800">U-roads</div>
                <div className="text-xs text-surface-500">82% of network</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">985km</div>
                <div className="text-sm text-surface-600">Footways</div>
                <div className="text-xs text-surface-500">also maintained</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              The borough also maintains 7km of segregated cycleways, 82km of public rights of way (estimated),
              12 road bridges, 15 footbridges, 6 subways and approximately 27,680 gullies cleaned on a reactive basis.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (81km — 14% of network): improving</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">14%</td>
                    <td className="px-4 py-2 text-center">81%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">3%</td>
                    <td className="px-4 py-2 text-center font-bold">19%</td>
                    <td className="px-4 py-2 text-center font-bold">78%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Principal road data is collected annually on behalf of Ealing by Transport for London.
              Credit where due: RED-condition A-roads have more than halved since 2020. But A-roads are
              only one-seventh of the borough's road network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (26km — 4% of network): broadly flat</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">10%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">61%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-amber-50/50">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">65%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">64%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9%</td>
                    <td className="px-4 py-2 text-center font-bold">26%</td>
                    <td className="px-4 py-2 text-center font-bold">65%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B and C road condition has barely moved: RED share was 10% in 2020 and 9% in 2024.
              Combined with amber-rated roads, roughly 35% of the classified local network needs — or will soon need — maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — But Below Steady-State</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£945k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.65m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">73.7%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Ealing spends nearly <span className="text-emerald-400">six times its DfT capital allocation</span> — 
              earning a GREEN spend scorecard. But projected 2025/26 capital and revenue spend totals roughly
              <span className="text-amber-400"> £7.66m</span>, still below the council's own £8.5m steady-state minimum.
              The chequebook is open; the network is still underfunded on the council's own model.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road majority */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 490km Residential Majority
          </h2>
          <p className="text-surface-600 mb-8">
            82% of Ealing's roads are unclassified — surveyed annually by walked inspection
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-surface-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No data collected</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Walked DVI Surveys</h3>
              <p className="text-surface-600 text-sm mb-3">
                Ealing collects unclassified road condition data annually through Detailed Visual Inspection
                surveys — a walked inspection by an independent surveyor identifying defects and scoring
                sections against the UK Pavement Management System standard.
              </p>
              <p className="text-surface-600 text-sm">
                Classified B and C roads are assessed using SCANNER laser-based technology. A-road condition
                is collected annually by TfL. There is no condition data at all for any road class in 2021.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">"Remaining Constant" — At 9-10% RED</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council's own analysis states the unclassified road trend is "remaining constant" —
                but simultaneously admits inflation has "significantly impacted" budgets in real terms,
                with population growth and new developments increasing demand on the network.
              </p>
              <p className="text-amber-800 text-sm">
                At 9% RED, roughly <strong>44km of residential streets</strong> should be considered for
                maintenance. That is not a network in recovery — it is one treading water.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Ealing's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your street last DVI-surveyed — and what condition score did it receive?</li>
              <li>• If 9% of U-roads are RED borough-wide, what was done about the defect on your road?</li>
              <li>• Did the council's walked survey identify your pothole before it damaged your vehicle?</li>
              <li>• With budgets below the £8.5m steady-state minimum, how quickly are RED-category streets scheduled?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits its network will decline without £8.5m a year cannot claim comprehensive
              knowledge of every defect on 490km of residential roads.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            17,297 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from Ealing's transparency report — down in the latest year, not gone
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">3,241</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,900</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,888</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,241</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">3,027</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17,297</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~9.5 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Ealing fills around 9.5 potholes per day across its 597km network.
                For 2025/26 the council estimates an average of 3,500 pothole repairs plus 500 additional
                pre-emptive locations that would not normally meet safety intervention levels — defects caught
                before they become reportable, but still evidence of a network generating failures.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2024/25 Drop Is Not Recovery</h3>
              <p className="text-amber-800 text-sm">
                Pothole fills fell from 4,241 to 3,027 in 2024/25 — a 29% reduction. That reflects
                repair volumes, not road quality. With 9% of U-roads still RED and the council projecting
                3,500 repairs for 2025/26, the network is still producing defects faster than preventative
                resurfacing can eliminate the underlying problem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Ealing Admits In Its Own Words
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim quotes from the July 2025 transparency report
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Based on modelling carried out in 2021 by an independent consultant, it was calculated that a minimum annual budget of £8.5m is required to maintain the network in a steady state at its current condition. Without this level of budget, the road network will continue to decline. We have not received any funding for Principal Road renewal from TfL in two years, therefore the additional DfT pothole funding is vital as we tend to use it to resurface our busiest roads and junctions."
              source="Ealing Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The trend indicated the unclassified roads is remaining constant based on the condition rating scores. However, inflation has significantly impacted upon our budgets in real terms and funding is increasingly stretched, with there being further implications in terms of population growth and substantial new developments, increasing demand for the network. The change to electric vehicles also increases the burden on the network, with heavier electric buses for example. The road network was not designed and built for such types."
              source="Ealing Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The TfL Funding Gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Ealing has received no TfL principal road renewal funding for two years. DfT pothole
                grants — rising from £291,000 to a projected £945,000 in 2025/26 — are now "vital" for
                resurfacing the borough's busiest roads and junctions.
              </p>
              <p className="text-surface-600 text-sm">
                That creates a documented dependency: main-road resurfacing relies on ring-fenced pothole
                pots, not steady principal renewal funding. A defect on a busy route may sit longer than
                the headline A-road improvement figures suggest.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">A Network Not Built For Today</h3>
              <p className="text-amber-800 text-sm mb-3">
                Ealing explicitly states its roads "were not designed and built" for heavier electric
                buses and modern traffic loads. Combined with climate pressures — hotter temperatures,
                wetter conditions, cold snaps — the council documents structural reasons defects keep forming.
              </p>
              <p className="text-amber-800 text-sm">
                That is not an excuse for individual potholes. It is an admission the network faces
                elevated deterioration risk — which raises what a "reasonable" inspection regime must deliver.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Maintenance Spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue figures from Table 2 — preventative share rising, reactive share still a quarter
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£4,249,416</td>
                  <td className="px-4 py-3 text-center">£1,998,815</td>
                  <td className="px-4 py-3 text-center">68.0%</td>
                  <td className="px-4 py-3 text-center">32.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£4,811,041</td>
                  <td className="px-4 py-3 text-center">£2,013,737</td>
                  <td className="px-4 py-3 text-center">70.5%</td>
                  <td className="px-4 py-3 text-center">29.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£5,176,423</td>
                  <td className="px-4 py-3 text-center">£1,947,096</td>
                  <td className="px-4 py-3 text-center">72.6%</td>
                  <td className="px-4 py-3 text-center">27.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£291,000</td>
                  <td className="px-4 py-3 text-center">£4,850,551</td>
                  <td className="px-4 py-3 text-center">£2,124,040</td>
                  <td className="px-4 py-3 text-center">69.5%</td>
                  <td className="px-4 py-3 text-center">30.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£291,000</td>
                  <td className="px-4 py-3 text-center">£5,566,168</td>
                  <td className="px-4 py-3 text-center">£2,160,508</td>
                  <td className="px-4 py-3 text-center">72.0%</td>
                  <td className="px-4 py-3 text-center">28.0%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£945,000</td>
                  <td className="px-4 py-3 text-center font-bold">£5,647,512</td>
                  <td className="px-4 py-3 text-center font-bold">£2,012,240</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">73.7%</td>
                  <td className="px-4 py-3 text-center font-bold">26.3%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">2025/26 Forward Plans</h3>
            <p className="text-surface-600 text-sm mb-3">
              DfT funding will be split with £500,000 for the borough-wide drive-and-repair pothole programme
              and £450,000 for plane-and-lay resurfacing. Planned resurfacing locations include Greenford Road,
              Uxbridge Road, Argyle Road, Rockware Avenue and Tentelow Lane. The council will resurface
              approximately 0.5km of carriageway under this programme.
            </p>
            <p className="text-surface-600 text-sm">
              0.5km of resurfacing against a 597km network puts the scale of the challenge in perspective —
              even with GREEN-rated spend, physical renewal reaches only a fraction of the borough each year.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Spend, AMBER-Condition Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Ealing invests seriously — but admits it is still not enough
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend nearly six times the DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 73.7% of projected spend classed as preventative maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road RED condition halved since 2020 (7% to 3%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual condition surveys on all road classes (DVI for U-roads, SCANNER for B/C, TfL for A-roads)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Drive-and-repair programme with pre-emptive intervention on emerging defects
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on well-maintained A-roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 9% of 490km of U-roads in RED, trend only "remaining constant"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits £8.5m steady-state minimum — projected spend ~£7.66m falls short
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No TfL principal road renewal funding for two years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 17,297 potholes filled in five years — defects still forming across the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — not the full GREEN profile of top-rated authorities
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Roads "not designed and built" for modern vehicle weights — documented deterioration risk
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but AMBER condition, your claim lives or dies on the
              <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 9% borough-wide RED rate and £8.5m funding gap are your structural arguments</li>
              <li>• Whether your road was on a 2025/26 drive-and-repair or resurfacing programme — or left out</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Ealing's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Ealing?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council still demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No £8.5m funding-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No U-road condition data cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ £8.5m steady-state admission cited</li>
                <li>✅ 9% U-road RED condition documented</li>
                <li>✅ 17,297 five-year pothole repairs referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Ealing</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=ealing"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Ealing Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Ealing has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend rating reflects that Ealing invests well beyond its £945,000 capital
                allocation. But your claim turns on the specific defect, not aggregate spend — and Ealing
                is AMBER on condition because B, C and unclassified roads are not improving. Section 58
                requires proof the council reasonably knew about and repaired the pothole that damaged your vehicle.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 490km — 82% of Ealing's 597km road network. At the last survey
                in 2024, 9% of U-roads were in RED condition, roughly 44km of residential streets. The council
                collects U-road condition data annually via walked Detailed Visual Inspection surveys, but admits
                the trend is only "remaining constant" while budgets are stretched in real terms.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the £8.5m steady-state budget admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Ealing's own transparency report states that a minimum annual budget of £8.5m is
                required to maintain the network at its current condition — and that without it, "the road
                network will continue to decline." Projected 2025/26 capital and revenue spend totals roughly
                £7.66m, below that threshold. That is a council admission it is underfunding maintenance
                relative to its own consultant's model.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                A-roads are improving — does that weaken claims on main roads?
              </h3>
              <p className="text-surface-600 text-sm">
                On principal routes, Ealing's position is stronger: RED-condition A-roads fell from 7% in
                2020 to 3% in 2024, with TfL collecting data annually. But the council also admits it has
                received no TfL principal road renewal funding for two years, relying on DfT pothole grants
                for its busiest resurfacing. A claim on an A-road still depends on whether the specific defect
                was reasonably inspected and repaired.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell to 3,027 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Ealing filled 3,027 potholes in 2024/25, down from 4,241 the year before. Over five
                years the council repaired 17,297 potholes — about 9.5 every day. For 2025/26 it estimates an
                average of 3,500 pothole repairs plus 500 additional pre-emptive locations that would not
                normally meet safety intervention levels.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the AMBER best-practice rating mean for Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                Ealing scores AMBER on best practice alongside AMBER on condition — not the full GREEN
                profile of better-rated authorities. The council does run preventative programmes (73.7% of
                projected 2025/26 spend) and annual condition surveys, but also documents inflation pressure,
                no TfL principal renewal funding for two years, and a network "not designed and built" for
                modern vehicle weights. Specificity — prior reports, photos, road class — matters more than
                the headline rating.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.ealing.gov.uk/downloads/download/8456/highways_maintenance_transparency_report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Ealing Council Local Highways Maintenance Transparency Report (July 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
